import React, { useCallback, useEffect, useState } from 'react';
import { Briefcase, CreditCard, DollarSign, Download, UserPlus } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import {
  CardWaitlistAudience,
  CardWaitlistEntry,
  CardWaitlistProduct,
  CardWaitlistStats,
  CardWaitlistStatus,
  cardWaitlistApi,
} from '../../api/card-waitlist';
import { PaginatedResponse } from '../../types';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';

const PAGE_SIZE = 25;

const productLabel: Record<CardWaitlistProduct, string> = {
  physical: 'Physical card',
  usd: 'Dollar card',
};

const audienceLabel: Record<CardWaitlistAudience, string> = {
  personal: 'Personal',
  business: 'Business',
};

const audienceBadge: Record<CardWaitlistAudience, 'info' | 'premium'> = {
  personal: 'info',
  business: 'premium',
};

const statusLabel: Record<CardWaitlistStatus, string> = {
  waiting: 'Waiting',
  invited: 'Invited',
  removed: 'Removed',
};

const statusBadge: Record<CardWaitlistStatus, 'warning' | 'success' | 'neutral'> = {
  waiting: 'warning',
  invited: 'success',
  removed: 'neutral',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const muted: React.CSSProperties = { color: 'var(--neutral-500)' };

const selectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };

/**
 * Who is waiting for a card the apps say is "Coming soon": physical cards,
 * and dollar cards.
 *
 * Unlike the marketing waitlist beside it, these are accounts: each row is
 * a signed-in person or a business that tapped Join in the app. So they can
 * be acted on. "Invited" records that somebody has been told the card is
 * ready for them; "Removed" takes them off, and they can join again from
 * the app. Listed in the order they joined, because that is the queue.
 */
export const CardWaitlist: React.FC = () => {
  const toast = useToast();
  const { can } = useAuth();
  const canManage = can('users.manage');
  const [search, setSearch] = useState('');

  usePage('Card Waitlist', {
    placeholder: 'Search by name, email, phone or business…',
    onSearch: setSearch,
  });

  const [stats, setStats] = useState<CardWaitlistStats | null>(null);
  const [data, setData] = useState<PaginatedResponse<CardWaitlistEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<CardWaitlistProduct | ''>('');
  const [audience, setAudience] = useState<CardWaitlistAudience | ''>('');
  const [status, setStatus] = useState<CardWaitlistStatus | ''>('waiting');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const filters = useCallback(
    () => ({
      search: search.trim() || undefined,
      product: product || undefined,
      audience: audience || undefined,
      status: status || undefined,
    }),
    [search, product, audience, status],
  );

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, listRes] = await Promise.all([
        cardWaitlistApi.stats(),
        cardWaitlistApi.list({ ...filters(), page, limit: PAGE_SIZE }),
      ]);
      setStats(statsRes);
      setData(listRes);
      // A selection is of the rows on screen. Carrying ids across a page or
      // a filter change would act on rows nobody can see.
      setPicked(new Set());
    } catch (err) {
      toast.error(err, 'Failed to load the card waitlist.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, toast]);

  useEffect(() => {
    setPage(1);
  }, [search, product, audience, status]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchEntries();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchEntries]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await cardWaitlistApi.exportCsv(filters());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `card_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err, 'Could not export the card waitlist.');
    } finally {
      setExporting(false);
    }
  };

  const move = async (ids: string[], next: CardWaitlistStatus) => {
    if (!ids.length) return;
    try {
      setSaving(true);
      const updated = await cardWaitlistApi.setStatus(ids, next);
      toast.success(
        `${updated === 1 ? '1 entry' : `${updated} entries`} marked ${statusLabel[next].toLowerCase()}.`,
      );
      await fetchEntries();
    } catch (err) {
      toast.error(err, 'Could not update the waitlist.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const items = data?.items ?? [];
  const allPicked = items.length > 0 && items.every((e) => picked.has(e.id));
  const waiting = stats?.waiting;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
          gap: 16,
        }}
      >
        <StatCard
          label="Waiting: physical, personal"
          value={waiting?.physical.personal ?? 0}
          icon={CreditCard}
          accent="blue"
        />
        <StatCard
          label="Waiting: physical, business"
          value={waiting?.physical.business ?? 0}
          icon={Briefcase}
          accent="purple"
        />
        <StatCard
          label="Waiting: dollar card"
          value={(waiting?.usd.personal ?? 0) + (waiting?.usd.business ?? 0)}
          icon={DollarSign}
          accent="green"
        />
        <StatCard
          label="Joined in the last 7 days"
          value={stats?.joinedLast7Days ?? 0}
          icon={UserPlus}
          accent="neutral"
        />
      </div>

      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <select
              aria-label="Card"
              value={product}
              onChange={(e) => setProduct(e.target.value as CardWaitlistProduct | '')}
              style={selectStyle}
            >
              <option value="">All cards</option>
              <option value="physical">Physical card</option>
              <option value="usd">Dollar card</option>
            </select>
            <select
              aria-label="Who"
              value={audience}
              onChange={(e) => setAudience(e.target.value as CardWaitlistAudience | '')}
              style={selectStyle}
            >
              <option value="">Personal and business</option>
              <option value="personal">Personal</option>
              <option value="business">Business</option>
            </select>
            <select
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as CardWaitlistStatus | '')}
              style={selectStyle}
            >
              <option value="">All statuses</option>
              <option value="waiting">Waiting</option>
              <option value="invited">Invited</option>
              <option value="removed">Removed</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canManage && picked.size > 0 ? (
              <>
                <Button
                  size="sm"
                  loading={saving}
                  onClick={() => void move(Array.from(picked), 'invited')}
                >
                  Mark {picked.size} invited
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={saving}
                  onClick={() => void move(Array.from(picked), 'removed')}
                >
                  Remove {picked.size}
                </Button>
              </>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              loading={exporting}
              disabled={!data || data.meta.total === 0}
              onClick={() => void handleExport()}
            >
              <Download size={14} style={{ marginRight: 6 }} />
              Export CSV
            </Button>
          </div>
        </div>

        {loading && !data ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : !items.length ? (
          <div style={{ padding: 48, textAlign: 'center', fontSize: 14, ...muted }}>
            {search || product || audience || status
              ? 'Nobody on the waitlist matches your filters'
              : 'Nobody has joined a card waitlist yet'}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}
              >
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {canManage ? (
                      <th style={{ ...th, width: 24 }}>
                        <input
                          type="checkbox"
                          aria-label="Select everyone on this page"
                          checked={allPicked}
                          onChange={() =>
                            setPicked(allPicked ? new Set() : new Set(items.map((e) => e.id)))
                          }
                        />
                      </th>
                    ) : null}
                    {['Joined', 'Who', 'Contact', 'Card', 'Status', ''].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((entry) => (
                    <tr key={entry.id} style={{ borderTop: '1px solid var(--neutral-200)' }}>
                      {canManage ? (
                        <td style={td}>
                          <input
                            type="checkbox"
                            aria-label={`Select ${entry.name}`}
                            checked={picked.has(entry.id)}
                            onChange={() => toggle(entry.id)}
                          />
                        </td>
                      ) : null}
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDate(entry.joinedAt)}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
                          {entry.name}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <Badge variant={audienceBadge[entry.audience]}>
                            {audienceLabel[entry.audience]}
                          </Badge>
                          {entry.tier ? (
                            <span style={{ ...muted, fontSize: 12, marginLeft: 8 }}>
                              {entry.tier.replace('_', ' ')}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td style={td}>
                        <div>{entry.email ?? <span style={muted}>No email</span>}</div>
                        <div style={muted}>{entry.phone ?? 'No phone'}</div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{productLabel[entry.product]}</td>
                      <td style={td}>
                        <Badge variant={statusBadge[entry.status]}>{statusLabel[entry.status]}</Badge>
                        {entry.invitedAt ? (
                          <div style={{ ...muted, fontSize: 12, marginTop: 4 }}>
                            {formatDate(entry.invitedAt)}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {canManage ? (
                          entry.status === 'waiting' ? (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={saving}
                                onClick={() => void move([entry.id], 'invited')}
                              >
                                Mark invited
                              </Button>{' '}
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={saving}
                                onClick={() => void move([entry.id], 'removed')}
                              >
                                Remove
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={saving}
                              onClick={() => void move([entry.id], 'waiting')}
                            >
                              Back to waiting
                            </Button>
                          )
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data!.meta.page}
              totalPages={data!.meta.totalPages}
              total={data!.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
};
